import React, { forwardRef, useId, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import type { NetworkNode } from '../types';
import { EntityAvatar } from './ui';

const fold = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

/** Entities matching a query: name prefix first, then word prefix, then anywhere in name or role line */
export function searchEntities(nodes: NetworkNode[], query: string, lineOf: (n: NetworkNode) => string, limit = 8): NetworkNode[] {
  const q = fold(query.trim());
  if (!q) return [];
  const scored: Array<[number, NetworkNode]> = [];
  nodes.forEach((n) => {
    const name = fold(n.label);
    let score = -1;
    if (name.startsWith(q)) score = 0;
    else if (name.split(/\s+/).some((w) => w.startsWith(q))) score = 1;
    else if (name.includes(q)) score = 2;
    else if (fold(lineOf(n)).includes(q)) score = 3;
    if (score >= 0) scored.push([score, n]);
  });
  return scored
    .sort((a, b) => a[0] - b[0] || a[1].label.localeCompare(b[1].label))
    .slice(0, limit)
    .map(([, n]) => n);
}

interface EntityPickerProps {
  nodes: NetworkNode[];
  onPick: (id: string) => void;
  lineOf: (n: NetworkNode) => string;
  placeholder?: string;
  exclude?: string[];
  autoFocus?: boolean;
  className?: string;
  /** Dropdown opens upwards (e.g. at the bottom of a panel) */
  dropUp?: boolean;
  onEscape?: () => void;
  onHover?: (id: string | null) => void;
  variant?: 'grey' | 'plain';
  ariaLabel?: string;
}

export const EntityPicker = forwardRef<HTMLInputElement, EntityPickerProps>(
  (
    { nodes, onPick, lineOf, placeholder = 'Search entities', exclude, autoFocus, className = '', dropUp, onEscape, onHover, variant = 'grey', ariaLabel },
    ref,
  ) => {
    const [query, setQuery] = useState('');
    const [active, setActive] = useState(0);
    const [open, setOpen] = useState(false);
    const listId = useId();
    const results = useMemo(
      () => searchEntities(exclude?.length ? nodes.filter((n) => !exclude.includes(n.id)) : nodes, query, lineOf),
      [nodes, query, lineOf, exclude],
    );

    const pick = (id: string) => {
      onPick(id);
      onHover?.(null);
      setQuery('');
      setOpen(false);
      setActive(0);
    };

    return (
      <div className={`relative ${className}`}>
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" strokeWidth={1.75} />
        <input
          ref={ref}
          type="search"
          role="combobox"
          aria-expanded={open && results.length > 0}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-label={ariaLabel || placeholder}
          autoFocus={autoFocus}
          value={query}
          placeholder={placeholder}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 120)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setActive((a) => Math.min(results.length - 1, a + 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActive((a) => Math.max(0, a - 1));
            } else if (e.key === 'Enter' && results[active]) {
              e.preventDefault();
              pick(results[active].id);
            } else if (e.key === 'Escape') {
              if (query) setQuery('');
              else onEscape?.();
              (e.target as HTMLInputElement).blur();
            }
          }}
          className={`block h-9 w-full rounded-full border-0 pl-9 pr-3 text-[16px] text-ink placeholder:text-neutral-400 transition-shadow focus:outline-none focus:ring-2 focus:ring-primary-600/25 sm:h-8 sm:text-[13px] ${
            variant === 'grey' ? 'bg-neutral-100 focus:bg-white' : 'bg-white ring-1 ring-inset ring-neutral-200'
          }`}
        />
        {open && query.trim() && (
          <ul
            id={listId}
            role="listbox"
            className={`absolute right-0 z-40 max-h-[320px] w-full min-w-[280px] animate-fade-up overflow-y-auto overscroll-contain rounded-2xl bg-white p-1 shadow-[0_16px_40px_-12px_rgba(0,0,0,0.22)] ring-1 ring-black/[0.06] ${
              dropUp ? 'bottom-full mb-1.5' : 'top-full mt-1.5'
            }`}
          >
            {results.length === 0 ? (
              <li className="px-3 py-2.5 text-[13px] text-neutral-400">No matches</li>
            ) : (
              results.map((n, i) => (
                <li key={n.id} role="option" aria-selected={i === active}>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pick(n.id)}
                    onMouseEnter={() => {
                      setActive(i);
                      onHover?.(n.id);
                    }}
                    onMouseLeave={() => onHover?.(null)}
                    className={`flex w-full items-center gap-2.5 rounded-xl px-2.5 py-1.5 text-left transition-colors ${i === active ? 'bg-neutral-100' : ''}`}
                  >
                    <EntityAvatar node={n} size="xs" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13px] text-ink">{n.label}</span>
                      <span className="block truncate text-[11.5px] text-neutral-500">{lineOf(n)}</span>
                    </span>
                  </button>
                </li>
              ))
            )}
          </ul>
        )}
      </div>
    );
  },
);
EntityPicker.displayName = 'EntityPicker';
